import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { MERCHANT_SERVICE_PROVIDERS, STATUSES } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import {
  buildMerchantServiceProviderFormData,
  buildMerchantServiceProviderRelationParams,
} from './merchantServiceProviderApiHelpers';

const getStatusLabel = (status) =>
  status?.label ?? status?.name ?? status?.title ?? String(status?.id ?? '');

const CreateMerchantServiceProvider = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [loadingPage, setLoadingPage] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [record, setRecord] = useState(null);

  const fetchStatuses = useCallback(async () => {
    setStatusLoading(true);
    try {
      let response;
      try {
        response = await api.get(STATUSES.BY_MODEL('MerchantServiceProvider'));
      } catch {
        try {
          response = await api.get(STATUSES.BY_MODEL('MerchantServiceProvider'));
        } catch {
          response = await api.get(STATUSES.LIST, { params: { paginate: 0 } });
        }
      }

      const list = extractList(response.data);
      const arr = Array.isArray(list) ? list : [];
      setStatusOptions(
        arr.map((status) => ({
          id: status.id,
          label: getStatusLabel(status),
          raw: status,
        }))
      );
    } catch {
      toast.error('Could not load statuses');
      setStatusOptions([]);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  useEffect(() => {
    if (!isEdit || !editId) {
      setLoadingPage(false);
      setRecord(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const { data } = await api.get(MERCHANT_SERVICE_PROVIDERS.BY_ID(editId), {
          params: buildMerchantServiceProviderRelationParams(),
        });
        const payload = data?.data ?? data;
        if (!cancelled) setRecord(payload);
      } catch (err) {
        toast.error(
          err.response?.data?.message ||
            'Failed to load merchant service provider'
        );
        navigate('/merchant-service-providers');
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, navigate]);

  useEffect(() => {
    if (!isEdit || !record) return;

    setName(record.name ?? '');
    setDescription(record.description ?? '');

    const nestedStatus =
      record.status && typeof record.status === 'object' ? record.status : null;
    const statusId = record.status_id ?? nestedStatus?.id ?? undefined;

    if (statusId) {
      if (nestedStatus) {
        const option = {
          id: statusId,
          label: getStatusLabel(nestedStatus),
          raw: nestedStatus,
        };
        setSelectedStatus(option);
        setStatusOptions((prev) =>
          prev.some((item) => String(item.id) === String(statusId))
            ? prev
            : [...prev, option]
        );
      } else {
        const option = statusOptions.find(
          (item) => String(item.id) === String(statusId)
        );
        setSelectedStatus(option ?? { id: statusId, label: String(statusId), raw: {} });
      }
    } else {
      setSelectedStatus(null);
    }
  }, [isEdit, record, statusOptions]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedStatus?.id || !name.trim()) {
      toast.warn('Select status and enter a name');
      return;
    }

    const values = {
      status_id: selectedStatus.id,
      name: name.trim(),
      description: description.trim(),
    };
    const formData = buildMerchantServiceProviderFormData(values);
    const query = new URLSearchParams(
      buildMerchantServiceProviderRelationParams()
    ).toString();

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(
          `${MERCHANT_SERVICE_PROVIDERS.BY_ID(editId)}?${query}`,
          formData
        );
        toast.success('Merchant service provider updated');
      } else {
        await api.post(MERCHANT_SERVICE_PROVIDERS.LIST, formData);
        toast.success('Merchant service provider created');
      }
      navigate('/merchant-service-providers');
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          (isEdit
            ? 'Failed to update merchant service provider'
            : 'Failed to create merchant service provider')
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/merchant-service-providers')}
          sx={{ mb: 2 }}
        >
          Back to merchant service providers
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit merchant service provider' : 'Create merchant service provider'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={(_, value) => setSelectedStatus(value)}
                  loading={statusLoading}
                  getOptionLabel={(option) => option?.label ?? ''}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id) === String(value?.id)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Status" required fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? (
                    <CircularProgress size={22} />
                  ) : isEdit ? (
                    'Save changes'
                  ) : (
                    'Create merchant service provider'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate('/merchant-service-providers')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateMerchantServiceProvider;
